import { useCallback, useEffect, useState } from 'react'

import { logger } from '@/utils/logger'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

const log = logger.child({ file: 'src/hooks/usePresenterSync.ts' })

interface UsePresenterSyncProps {
  lessonId: string
  slide: number
  totalSlides: number
}

export const usePresenterSync = ({
  lessonId,
  slide,
  totalSlides,
}: UsePresenterSyncProps) => {
  const navigate = useNavigate()
  const { i18n } = useTranslation()
  const [presenterConnected, setPresenterConnected] = useState(false)

  // Sync presenter view when URL changes (e.g., browser back/forward, direct navigation)
  useEffect(() => {
    // Get the presenter window reference that was stored by the lesson summary
    const presenterWindow = (window as any).presenterWindow

    if (presenterWindow && !presenterWindow.closed) {
      log.info(
        { slide },
        '🔄 Audience: URL changed, syncing presenter to match slide',
      )
      try {
        presenterWindow.postMessage(
          {
            type: 'slide-change',
            slide: slide,
          },
          '*',
        )
        log.info(
          { slide },
          '✅ Audience: Successfully synced presenter to slide',
        )
      } catch (error) {
        log.error({ error }, '❌ Audience: Could not sync presenter view')
        // Clear the stale presenter window reference
        delete (window as any).presenterWindow
      }
    } else {
      log.warn('⚠️ Audience: No presenter window found to sync')
      // Try to reconnect by looking for the presenter window
      const windows = window.opener ? [window.opener] : []
      const foundPresenter = windows.find(
        (win) =>
          win && !win.closed && win.location.pathname === '/presentation',
      )

      if (foundPresenter) {
        log.info('🔗 Audience: Found presenter window, reconnecting...')
        ;(window as any).presenterWindow = foundPresenter
        // Try to sync again
        try {
          foundPresenter.postMessage(
            {
              type: 'slide-change',
              slide: slide,
            },
            '*',
          )
          log.info('✅ Audience: Successfully reconnected and synced')
        } catch (error) {
          log.error({ error }, '❌ Audience: Failed to sync after reconnection')
        }
      } else {
        log.warn(
          '⚠️ Audience: No presenter window found, running in standalone mode',
        )
      }
    }
  }, [slide])

  // Sync language changes with presenter view
  useEffect(() => {
    if (presenterConnected) {
      syncLanguageWithPresenter(i18n.language)
    }
  }, [i18n.language, presenterConnected])

  const syncLanguageWithPresenter = (language: string) => {
    const presenterWindow = (window as any).presenterWindow
    if (presenterWindow && !presenterWindow.closed) {
      log.info(
        { language },
        '🔄 Audience: Sending language change to presenter',
      )
      try {
        presenterWindow.postMessage(
          {
            type: 'language-change',
            language: language,
          },
          '*',
        )
        log.info('✅ Audience: Successfully sent language change to presenter')
      } catch (error) {
        log.error(
          { error },
          '❌ Audience: Could not sync language with presenter',
        )
      }
    }
  }

  // Stop presentation and navigate back to lesson summary
  const stopPresentation = useCallback(() => {
    const presenterWindow = (window as any).presenterWindow
    if (presenterWindow && !presenterWindow.closed) {
      presenterWindow.postMessage({ type: 'presentation-stop' }, '*')
    }
    navigate({
      to: '/lesson/$lessonId',
      params: { lessonId },
      search: { tab: 'presentation' },
    })
  }, [navigate, lessonId])

  // Listen for messages from presenter view
  useEffect(() => {
    const abortController = new AbortController()
    const { signal } = abortController

    const handleMessage = (event: MessageEvent) => {
      log.info({ data: event.data }, '📨 Audience: Received message')
      if (event.data.type === 'slide-change') {
        const slideNumber = event.data.slide
        log.info({ slideNumber }, '🔄 Audience: Updating slide to')
        if (slideNumber >= 1 && slideNumber <= totalSlides) {
          // Update URL using TanStack Router
          navigate({
            to: '/lesson/$lessonId/audience',
            params: { lessonId },
            search: { slide: slideNumber },
            replace: true,
          })
          log.info('✅ Audience: Successfully updated slide to:', slideNumber)
        } else {
          log.warn('❌ Audience: Invalid slide number:', slideNumber)
        }
      } else if (event.data.type === 'presentation-stop') {
        log.info('🛑 Audience: Presentation stopped by presenter')
        // Navigate back to lesson summary
        navigate({
          to: '/lesson/$lessonId',
          params: { lessonId },
          search: { tab: 'presentation' },
        })
      } else if (event.data.type === 'language-change') {
        const language = event.data.language
        log.info(
          { language },
          '🔄 Audience: Received language change from presenter',
        )
        if (language && i18n.language !== language) {
          i18n.changeLanguage(language)
          log.info('✅ Audience: Successfully changed language to', language)
        }
      }

      // Set presenter as connected when we receive any message
      if (event.data.type) {
        setPresenterConnected(true)
      }
    }

    window.addEventListener('message', handleMessage, { signal })
    return () => {
      abortController.abort()
    }
  }, [navigate, i18n, lessonId, totalSlides])

  return {
    presenterConnected,
    syncLanguageWithPresenter,
    stopPresentation,
  }
}
